import React from 'react';
import { Helmet } from 'react-helmet-async';

const AdvancedSEO = ({ 
  title,
  description,
  keywords,
  url,
  structuredData,
  localBusinessData,
  breadcrumbs,
  faqData,
  serviceData,
  includeFAQSchema = true
}) => {
  const createStructuredData = () => {
    const schemas = [];

    // Local Business Schema (Enhanced)
    if (localBusinessData) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": "https://nypestexterminating.com/#localbusiness",
        "name": "NY Pest & Exterminating",
        "alternateName": ["NY Pest Control", "NY Exterminating", "New York Pest Control"],
        "description": "Professional eco-friendly pest control services in New York City. Specializing in rodent control, bed bug treatment, termite inspection, and commercial pest management. Licensed, insured, and Green Shield certified.",
        "url": "https://nypestexterminating.com",
        "image": [
          "https://customer-assets.emergentagent.com/job_ecopestcontrol/artifacts/7osc181x_WhyProfessionalPestControlServicesareVitalforHomes.jpg",
          "https://nypestexterminating.com/images/ny-pest-logo.png"
        ],
        "logo": "https://nypestexterminating.com/images/ny-pest-logo.png",
        "telephone": "+16462099955",
        "email": "info@nypestexterminating.com",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+16462099955",
          "contactType": "customer service",
          "availableLanguage": ["English", "Spanish"],
          "contactOption": ["TollFree"],
          "areaServed": ["NY", "NYC", "Brooklyn", "Manhattan", "Queens", "Bronx", "Staten Island"]
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "New York City",
          "addressLocality": "New York",
          "addressRegion": "NY",
          "postalCode": "11691",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "40.7128",
          "longitude": "-74.0060"
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "00:00",
            "closes": "23:59",
            "validFrom": "2024-01-01",
            "validThrough": "2025-12-31"
          }
        ],
        "priceRange": "$$",
        "paymentAccepted": ["Cash", "Credit Card", "Check", "PayPal"],
        "currenciesAccepted": "USD",
        "areaServed": [
          {
            "@type": "City",
            "name": "New York",
            "sameAs": "https://en.wikipedia.org/wiki/New_York_City"
          },
          {
            "@type": "Borough",
            "name": "Brooklyn",
            "containedInPlace": "New York, NY"
          },
          {
            "@type": "Borough",
            "name": "Manhattan",
            "containedInPlace": "New York, NY"
          },
          {
            "@type": "Borough",
            "name": "Queens",
            "containedInPlace": "New York, NY"
          },
          {
            "@type": "Borough",
            "name": "The Bronx",
            "containedInPlace": "New York, NY"
          },
          {
            "@type": "Borough",
            "name": "Staten Island",
            "containedInPlace": "New York, NY"
          },
          {
            "@type": "AdministrativeArea",
            "name": "Nassau County",
            "containedInPlace": "New York, NY"
          },
          {
            "@type": "AdministrativeArea",
            "name": "Suffolk County",
            "containedInPlace": "New York, NY"
          },
          {
            "@type": "AdministrativeArea",
            "name": "Westchester County",
            "containedInPlace": "New York, NY"
          }
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Pest Control Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Rodent Control & Removal",
                "description": "Professional rat and mice extermination services",
                "provider": {
                  "@id": "https://nypestexterminating.com/#localbusiness"
                }
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Bed Bug Extermination",
                "description": "Heat treatment and chemical bed bug removal",
                "provider": {
                  "@id": "https://nypestexterminating.com/#localbusiness"
                }
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Termite Inspection & Treatment",
                "description": "Comprehensive termite inspection and treatment services",
                "provider": {
                  "@id": "https://nypestexterminating.com/#localbusiness"
                }
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Commercial Pest Control",
                "description": "Restaurant, office, and commercial property pest management",
                "provider": {
                  "@id": "https://nypestexterminating.com/#localbusiness"
                }
              }
            }
          ]
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "127",
          "bestRating": "5",
          "worstRating": "1"
        },
        "review": [
          {
            "@type": "Review",
            "reviewRating": {
              "@type": "Rating",
              "ratingValue": "5"
            },
            "author": {
              "@type": "Person",
              "name": "Sarah M."
            },
            "reviewBody": "Excellent service! They eliminated our mouse problem quickly and safely. Very professional and eco-friendly approach."
          }
        ],
        "sameAs": [
          "https://www.facebook.com/nypestexterminating",
          "https://www.instagram.com/nypestexterminating",
          "https://www.linkedin.com/company/nypestexterminating",
          "https://www.yelp.com/biz/ny-pest-exterminating"
        ]
      });
    }

    // Organization Schema
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://nypestexterminating.com/#organization",
      "name": "NY Pest & Exterminating",
      "legalName": "NY Pest & Exterminating LLC",
      "url": "https://nypestexterminating.com",
      "logo": "https://nypestexterminating.com/images/ny-pest-logo.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+16462099955",
        "contactType": "customer service"
      },
      "sameAs": [
        "https://www.facebook.com/nypestexterminating",
        "https://www.instagram.com/nypestexterminating"
      ]
    });

    // Website Schema
    schemas.push({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://nypestexterminating.com/#website",
      "url": "https://nypestexterminating.com",
      "name": "NY Pest & Exterminating",
      "description": "Professional eco-friendly pest control services in NYC",
      "publisher": {
        "@id": "https://nypestexterminating.com/#organization"
      },
      "potentialAction": [
        {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://nypestexterminating.com/search?q={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        }
      ]
    });

    // Breadcrumbs if provided
    if (breadcrumbs && breadcrumbs.length > 1) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.name,
          "item": crumb.url
        }))
      });
    }

    // FAQ Schema if provided (can be disabled to prevent duplicates)
    if (includeFAQSchema && faqData && faqData.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      });
    }

    // Service Schema if provided
    if (serviceData) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": serviceData.name,
        "description": serviceData.description,
        "provider": {
          "@id": "https://nypestexterminating.com/#localbusiness"
        },
        "areaServed": {
          "@type": "City",
          "name": "New York City"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": serviceData.name,
          "itemListElement": serviceData.offers || []
        }
      });
    }

    return schemas;
  };

  const schemas = createStructuredData();

  return (
    <Helmet>
      {/* Enhanced Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Google Search Console Verification */}
      <meta name="google-site-verification" content="google918c964fb906c27b" />
      
      {/* Advanced SEO Meta Tags */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* Local SEO */}
      <meta name="geo.region" content="US-NY" />
      <meta name="geo.placename" content="New York City" />
      <meta name="geo.position" content="40.7128;-74.0060" />
      <meta name="ICBM" content="40.7128, -74.0060" />
      <meta name="DC.title" content={title} />
      
      {/* Business Information */}
      <meta name="contact:phone_number" content="+16462099955" />
      <meta name="contact:email" content="info@nypestexterminating.com" />
      <meta name="business:contact_data:street_address" content="New York City, NY" />
      <meta name="business:contact_data:locality" content="New York" />
      <meta name="business:contact_data:region" content="NY" />
      <meta name="business:contact_data:postal_code" content="11691" />
      <meta name="business:contact_data:country_name" content="USA" />
      
      {/* Enhanced Open Graph */}
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="business.business" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="NY Pest & Exterminating" />
      <meta property="og:image" content="https://customer-assets.emergentagent.com/job_ecopestcontrol/artifacts/7osc181x_WhyProfessionalPestControlServicesareVitalforHomes.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="NY Pest & Exterminating - Professional Eco-Friendly Pest Control NYC" />
      
      {/* Business Open Graph */}
      <meta property="business:contact_data:street_address" content="New York City" />
      <meta property="business:contact_data:locality" content="New York" />
      <meta property="business:contact_data:region" content="NY" />
      <meta property="business:contact_data:postal_code" content="11691" />
      <meta property="business:contact_data:country_name" content="USA" />
      <meta property="business:contact_data:phone_number" content="+16462099955" />
      
      {/* Enhanced Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_ecopestcontrol/artifacts/7osc181x_WhyProfessionalPestControlServicesareVitalforHomes.jpg" />
      <meta name="twitter:image:alt" content="Professional pest control technician in protective gear" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Preload Critical Resources */}
      <link rel="preload" href="/fonts/inter.woff2" as="font" type="font/woff2" crossOrigin="anonymous" />
      
      {/* DNS Prefetch */}
      <link rel="dns-prefetch" href="//customer-assets.emergentagent.com" />
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      
      {/* Apple-Specific */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="NY Pest Control" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      
      {/* Microsoft */}
      <meta name="msapplication-TileColor" content="#8FEC78" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      
      {/* Structured Data */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default AdvancedSEO;